import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  inject,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { QuillEditorComponent } from 'ngx-quill';
import { SearchService } from '../../../pages/search/services/search.service';
import { LeadNote } from '../../../pages/leads/models/lead-note';
import { AuthService } from '../../../pages/auth/services/auth.service';
import { PopupComponent } from '../popup/popup.component';
import { DestroyBase } from '../../base-classes/destroy-component-base';
import { forkJoin, takeUntil } from 'rxjs';
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import { ConnectionWithSharedNote } from '../../../pages/outreach/models/connection.model';
import { SharedConnectionUsers } from '../../models/shared-connection-users';
import { LeadFileService } from '../../../pages/leads/services/lead-file.service';
import { LeadFile } from '../../../pages/leads/models/lead-file';
import { ClickOutsideDirective } from '../../directives/click-outside/click-outside.directive';
import { AuditLogService } from '../../services/audit-log/audit-log.service';
import { FileUploadService } from '../../../pages/documents/services/file-upload.service';
import { ContainerSasUrl } from '../../interfaces/company';
import { ImageService } from '../../services/image-service/image.service';
import { ToastType } from '../../interfaces/common';
import { CommonService } from '../../services/common/common.service';
import { EntityType, ReportContentData } from '../../interfaces/report-content';
import { ReportContentService } from '../../services/reports/report-content.service';

@Component({
  selector: 'app-record-notes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    QuillEditorComponent,
    DatePipe,
    PopupComponent,
    SafeHtmlPipe,
    ClickOutsideDirective,
  ],
  templateUrl: './record-notes.component.html',
  styleUrl: './record-notes.component.scss',
})
export class RecordNotesComponent
  extends DestroyBase
  implements OnInit, OnDestroy
{
  @Input({ required: true }) uniqueId!: string;
  @Input() companyName: string | null = null;
  @Output() closePopup = new EventEmitter<void>();

  private searchService = inject(SearchService);
  private authService = inject(AuthService);
  private auditLogService = inject(AuditLogService);
  private leadFileService = inject(LeadFileService);
  private fileUploadService = inject(FileUploadService);
  private imageService = inject(ImageService);
  private commonService = inject(CommonService);
  private reportContentService = inject(ReportContentService);

  reportNote(note: LeadNote): void {
    const payload: ReportContentData = {
      entityId: note.id,
      entityType: EntityType.NOTE,
      title: 'Flag Note',
      itemTitle: '',
    };
    this.onClose();
    this.reportContentService.open(payload);
  }

  notes: LeadNote[] = [];
  isLoading = false;
  isCreating = false;
  isSaving = false;
  editingNoteId: number | null = null;
  newNoteContent = '';
  editNoteContent = '';
  showDeleteConfirm: number | null = null;
  currentUser = '';
  expandedNotes = new Set<number>();
  assignedRecordConnections: ConnectionWithSharedNote[] = [];
  sharingNoteId: number | null = null;
  connectionCheckboxState: Record<string, boolean> = {};
  originalConnectionCheckboxState: Record<string, boolean> = {};
  userSuggestions!: SharedConnectionUsers[];
  isLoadingUsers = true;
  isLoadingMore = false;
  isSharing = false;
  selectedPeopleToShare: string[] = [];
  selectednoteToShare: number | null = null;

  showFilePicker = false;
  leadFiles: LeadFile[] = [];
  isLoadingFiles = false;

  // Quill editor configuration
  quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ color: [] }, { background: [] }],
    ],
  };

  containerSasUrl!: ContainerSasUrl;
  profileImageUrl: string | null = null;

  ngOnInit() {
    // Disable body scroll when popup opens
    document.body.style.overflow = 'hidden';
    this.imageService.profileImageUrl$
      .pipe(takeUntil(this.destroy$))
      .subscribe((url) => {
        this.profileImageUrl = url;
      });
    this.getCurrentUser();
    this.loadNotes();
  }

  override ngOnDestroy() {
    // Re-enable body scroll when popup closes
    document.body.style.overflow = '';
    super.ngOnDestroy();
  }

  private getCurrentUser() {
    const userInfo = this.authService.getLoggedInUserInfo();
    this.currentUser = userInfo?.username || '';
  }

  getProfileImageUrl(
    path: string | null,
    username: string | undefined,
  ): string {
    if (this.currentUser === username && this.profileImageUrl) {
      return this.profileImageUrl;
    }
    if (!this.containerSasUrl || !path) return '';
    return `${path}?${this.containerSasUrl.sas_token}`;
  }

  getImageUrl(path: string): string {
    if (!this.containerSasUrl || !path) return '';
    return `${path}?${this.containerSasUrl.sas_token}`;
  }

  loadNotes() {
    this.isLoading = true;

    forkJoin({
      notes: this.searchService.getRecordNotes(this.uniqueId),
      sasUrl: this.imageService.getContainerSasUrl(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ notes, sasUrl }) => {
          this.containerSasUrl = sasUrl;

          this.notes = notes.sort((a, b) => {
            // Always put current user's note at the top
            const aIsCurrentUser = a.user.username === this.currentUser;
            const bIsCurrentUser = b.user.username === this.currentUser;

            if (aIsCurrentUser && !bIsCurrentUser) return -1;
            if (!aIsCurrentUser && bIsCurrentUser) return 1;

            // For all other notes, sort by updated date (newest first)
            return (
              new Date(b.updated_on_utc).getTime() -
              new Date(a.updated_on_utc).getTime()
            );
          });
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading notes:', error);
          this.isLoading = false;
        },
      });
  }

  startCreatingNote() {
    this.isCreating = true;
    this.newNoteContent = '';
  }

  cancelCreating() {
    this.isCreating = false;
    this.newNoteContent = '';
  }

  saveNewNote() {
    if (!this.newNoteContent || !this.newNoteContent.trim() || this.isSaving)
      return;

    this.auditLogService.logFeature({
      featureName: 'add_edit_lead_note',
      action: 'add',
      meta: {
        recordId: this.uniqueId,
        recordName: this.companyName || '',
      },
      description: 'User creates a new lead note',
    });

    this.isSaving = true;
    this.searchService
      .createRecordNote(this.uniqueId, this.newNoteContent)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (note) => {
          this.notes.unshift(note);
          // Re-sort to ensure current user note stays at top
          this.notes = this.notes.sort((a, b) => {
            const aIsCurrentUser = a.user.username === this.currentUser;
            const bIsCurrentUser = b.user.username === this.currentUser;

            if (aIsCurrentUser && !bIsCurrentUser) return -1;
            if (!aIsCurrentUser && bIsCurrentUser) return 1;

            return (
              new Date(b.updated_on_utc).getTime() -
              new Date(a.updated_on_utc).getTime()
            );
          });
          this.isCreating = false;
          this.newNoteContent = '';
          this.isSaving = false;
          this.showToastMessage(
            'Note created successfully!',
            ToastType.Success,
          );
        },
        error: (error) => {
          console.error('Error creating note:', error);
          this.showToastMessage(
            'Failed to create note. Please try again.',
            ToastType.Error,
          );
          // Close the editor even on error
          this.isCreating = false;
          this.newNoteContent = '';
          this.isSaving = false;
        },
      });
  }

  getAssignedRecordConnections(uniqueId: string, note: LeadNote) {
    this.searchService
      .getAssignedRecordConnections(uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (connections: ConnectionWithSharedNote[]) => {
          this.assignedRecordConnections = connections;
          this.sharingNoteId = note.id;
          this.isLoadingUsers = false;
          // Initialize checkbox state from is_shared
          this.assignedRecordConnections.forEach((conn) => {
            this.connectionCheckboxState[conn.connection_name] = conn.is_shared;
          });
          // Store original state for comparison
          this.originalConnectionCheckboxState = {
            ...this.connectionCheckboxState,
          };
        },
        error: (error) => {
          console.error('Error fetching assigned record connections:', error);
          this.isLoadingUsers = false;
        },
      });
  }

  shareNote(uniqueId: string, note: LeadNote) {
    this.getAssignedRecordConnections(uniqueId, note);
    this.connectionCheckboxState = {};
    this.sharingNoteId = note.id;
    this.isLoadingUsers = true;
    this.selectednoteToShare = note.id;
  }

  startEditingNote(note: LeadNote) {
    this.editingNoteId = note.id;
    this.editNoteContent = note.note;
  }

  cancelEditing() {
    this.editingNoteId = null;
    this.editNoteContent = '';
  }

  saveEditedNote() {
    if (
      !this.editNoteContent ||
      !this.editNoteContent.trim() ||
      !this.editingNoteId ||
      this.isSaving
    )
      return;

    this.auditLogService.logFeature({
      featureName: 'add_edit_lead_note',
      action: 'update',
      meta: {
        recordId: this.uniqueId,
        recordName: this.companyName || '',
        noteId: String(this.editingNoteId),
      },
      description: 'User updates a lead note',
    });

    this.isSaving = true;
    this.searchService
      .updateRecordNote(
        this.uniqueId,
        this.editingNoteId.toString(),
        this.editNoteContent,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedNote) => {
          const index = this.notes.findIndex(
            (n) => n.id === this.editingNoteId,
          );
          if (index !== -1) {
            this.notes[index] = updatedNote;
            // Re-sort to ensure current user note stays at top
            this.notes = this.notes.sort((a, b) => {
              const aIsCurrentUser = a.user.username === this.currentUser;
              const bIsCurrentUser = b.user.username === this.currentUser;

              if (aIsCurrentUser && !bIsCurrentUser) return -1;
              if (!aIsCurrentUser && bIsCurrentUser) return 1;

              return (
                new Date(b.updated_on_utc).getTime() -
                new Date(a.updated_on_utc).getTime()
              );
            });
          }
          this.editingNoteId = null;
          this.editNoteContent = '';
          this.isSaving = false;
          this.showToastMessage(
            'Note updated successfully!',
            ToastType.Success,
          );
        },
        error: (error) => {
          console.error('Error updating note:', error);
          this.showToastMessage(
            'Failed to update note. Please try again.',
            ToastType.Error,
          );
          // Close the editor even on error
          this.editingNoteId = null;
          this.editNoteContent = '';
          this.isSaving = false;
        },
      });
  }

  confirmDelete(noteId: number) {
    this.showDeleteConfirm = noteId;
  }

  cancelDelete() {
    this.showDeleteConfirm = null;
  }

  deleteNote() {
    if (!this.showDeleteConfirm) return;

    this.auditLogService.logFeature({
      featureName: 'lead_notes',
      action: 'delete',
      meta: {
        recordId: this.uniqueId,
        recordName: this.companyName || '',
        noteId: String(this.showDeleteConfirm),
      },
      description: 'User deletes a lead note',
    });

    this.searchService
      .deleteRecordNote(this.uniqueId, this.showDeleteConfirm.toString())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notes = this.notes.filter(
            (n) => n.id !== this.showDeleteConfirm,
          );
          this.showDeleteConfirm = null;
          this.showToastMessage(
            'Note deleted successfully!',
            ToastType.Success,
          );
        },
        error: (error) => {
          console.error('Error deleting note:', error);
          this.showToastMessage(
            'Failed to delete note. Please try again.',
            ToastType.Error,
          );
          this.showDeleteConfirm = null;
        },
      });
  }

  canEditNote(note: LeadNote): boolean {
    return note.user.username === this.currentUser;
  }

  hasCurrentUserNote(): boolean {
    return this.notes.some((note) => note.user.username === this.currentUser);
  }

  getCurrentUserNote(): LeadNote | undefined {
    return this.notes.find((note) => note.user.username === this.currentUser);
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleString();
  }

  onClose() {
    // Re-enable body scroll when popup is manually closed
    document.body.style.overflow = '';
    this.closePopup.emit();
  }

  getPlainText(htmlContent: string): string {
    const div = document.createElement('div');
    div.innerHTML = htmlContent;
    return div.textContent || div.innerText || '';
  }

  processNoteContent(htmlContent: string): string {
    if (!htmlContent) return '';

    const div = document.createElement('div');
    div.innerHTML = htmlContent;

    const links = div.querySelectorAll('a');
    links.forEach((link) => {
      const href = link.getAttribute('href');
      if (href) {
        if (
          !href.startsWith('http://') &&
          !href.startsWith('https://') &&
          !href.startsWith('mailto:')
        ) {
          link.setAttribute('href', `https://${href}`);
        }
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      }
    });

    // Replace file tokens [file:id:name]
    let html = div.innerHTML;
    const fileTokenRegex = /\[file:(\d+):(.*?)\]/g;
    html = html.replace(fileTokenRegex, (match, id, name) => {
      return `<button type="button" class="file-chip" data-file-id="${id}" title="Click to download">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width: 14px; height: 14px; margin-right: 4px; vertical-align: middle;">
                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                <polyline points="13 2 13 9 20 9"></polyline>
            </svg>
            ${name}
        </button>`;
    });

    // Update div content with replaced html
    div.innerHTML = html;

    return div.innerHTML;
  }

  handleNoteInteraction(event: Event) {
    if (
      event instanceof KeyboardEvent &&
      event.key !== 'Enter' &&
      event.key !== ' '
    ) {
      return;
    }

    const target = event.target as HTMLElement;
    // Check if interaction was on a file-chip or inside it
    const chip = target.closest('.file-chip') as HTMLElement;
    if (chip && chip.dataset['fileId']) {
      event.preventDefault();
      event.stopPropagation();
      const fileId = Number(chip.dataset['fileId']);
      this.downloadFile(fileId);
    }
  }

  downloadFile(fileId: number) {
    this.fileUploadService
      .downloadFile(fileId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          window.open(res.download_url, '_blank');
        },
        error: (err) => {
          console.error('Download failed', err);
          this.showToastMessage('Failed to download file.', ToastType.Error);
        },
      });
  }

  toggleNoteExpansion(noteId: number) {
    if (this.expandedNotes.has(noteId)) {
      this.expandedNotes.delete(noteId);
    } else {
      this.expandedNotes.add(noteId);
    }
  }

  isNoteExpanded(noteId: number): boolean {
    return this.expandedNotes.has(noteId);
  }

  shouldShowToggle(content: string): boolean {
    if (!content) return false;

    // Create a temporary div to measure content height
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.width = '100%';
    tempDiv.style.lineHeight = '1.5';
    tempDiv.style.fontSize = '14px';
    document.body.appendChild(tempDiv);

    const height = tempDiv.scrollHeight;
    document.body.removeChild(tempDiv);

    // Show toggle if content height exceeds ~120px (roughly 5 lines)
    return height > 120;
  }

  private showToastMessage(message: string, type: ToastType) {
    this.commonService.triggerToast({
      type: type,
      message: message,
    });
  }

  getConnectionDisplayName(note: LeadNote): string {
    if (!note.connection) return '';
    return (
      note.connection.display_name || note.connection.connection_name || ''
    );
  }

  onCloseUsersDropDown(): void {
    this.sharingNoteId = null;
    this.connectionCheckboxState = {};
    this.originalConnectionCheckboxState = {};
  }

  getSelectedConnections(): string[] {
    return Object.entries(this.connectionCheckboxState)
      .filter(([, isChecked]) => isChecked)
      .map(([connection]) => connection);
  }

  removeConnection(connection: string): void {
    this.connectionCheckboxState[connection] = false;
  }

  onUsersSelection(event: Event, assignee: string, isConnection = false): void {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    if (isConnection) {
      this.connectionCheckboxState[assignee] = isChecked;
    }
  }

  shareWithSelectedPeople(): void {
    this.isSharing = true;

    const selectedConnectionIds = Object.entries(this.connectionCheckboxState)
      .filter(([, isChecked]) => isChecked)
      .map(([connection]) => {
        const connectionObj = this.assignedRecordConnections.find(
          (conn) => conn.connection_name === connection,
        );
        return connectionObj?.id;
      })
      .filter((id): id is number => !!id); // Filter out undefined values

    if (this.selectednoteToShare !== null) {
      this.searchService
        .shareNote(selectedConnectionIds, this.selectednoteToShare)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isSharing = false;
            this.sharingNoteId = null;
            this.showToastMessage(
              'Note sharing info updated successfully!',
              ToastType.Success,
            );
          },
          error: () => {
            this.isSharing = false;
            this.sharingNoteId = null;
            this.showToastMessage(
              'Failed to update note sharing info. Please try again!',
              ToastType.Error,
            );
          },
        });
    } else {
      this.isSharing = false;
      this.showToastMessage('No note selected to share!', ToastType.Error);
    }
  }

  hasShareChanges(): boolean {
    // Compare current state with original state
    const currentState = this.connectionCheckboxState;
    const originalState = this.originalConnectionCheckboxState;

    // Check if any connection's sharing state has changed
    for (const connectionName of Object.keys(currentState)) {
      if (currentState[connectionName] !== originalState[connectionName]) {
        return true;
      }
    }

    return false;
  }

  openFilePicker() {
    this.showFilePicker = true;
    this.isLoadingFiles = true;
    this.leadFileService
      .getLeadFiles(this.uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.leadFiles = res.results;
          this.isLoadingFiles = false;
        },
        error: (err) => {
          console.error('Failed to load files', err);
          this.isLoadingFiles = false;
        },
      });
  }

  closeFilePicker() {
    this.showFilePicker = false;
  }

  private appendTokenToHtml(
    htmlContent: string | null | undefined,
    token: string,
  ): string {
    const content = (htmlContent || '').trim();

    // Treat empty Quill editor blocks nicely
    if (content === '<p><br></p>') {
      return `<p>${token}</p>`;
    }

    // Insert the token before the closing </p> so it renders on the same line
    if (content.endsWith('</p>')) {
      // If there is a trailing line break before the closing paragraph, remove it
      if (content.endsWith('<br></p>')) {
        return content.slice(0, -8) + ' ' + token + '</p>';
      }
      return content.slice(0, -4) + ' ' + token + '</p>';
    }

    // Fallback if not wrapped in standard tags
    return content + (content ? ' ' : '') + token;
  }

  selectFileToAttach(file: LeadFile) {
    const fileName = file.file.file_name;

    const fileId = file.file.id;
    const token = `[file:${fileId}:${fileName}]`;

    if (this.isCreating) {
      this.newNoteContent = this.appendTokenToHtml(this.newNoteContent, token);
    } else if (this.editingNoteId) {
      this.editNoteContent = this.appendTokenToHtml(
        this.editNoteContent,
        token,
      );
    }
    this.closeFilePicker();
  }
}
